import {
    Container, FormControl, Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Button from '../foundation/Button';

type CompanyCoverProps = {
    $imageUrl?: string;
};

export const CompanyPageContainer = styled(Container)(({ theme }) => ({
    paddingBlock: theme.spacing(3, 7),
    [theme.breakpoints.down('sm')]: {
        paddingBlock: theme.spacing(2, 5),
    },
}));

export const CompanyHeader = styled('section')({
    position: 'relative',
});

export const CompanyCover = styled('div', {
    shouldForwardProp: prop => prop !== '$imageUrl',
})<CompanyCoverProps>(({ theme, $imageUrl }) => ({
    backgroundColor: '#dfe9e5',
    backgroundImage: $imageUrl
        ? `linear-gradient(180deg, rgba(5, 26, 41, 0.08), rgba(5, 26, 41, 0.24)), url("${$imageUrl}")`
        : `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, #1c5264 48%, ${theme.palette.primary.main} 100%)`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    borderRadius: 24,
    minHeight: 320,
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '50%',
        content: '""',
        height: 260,
        insetInlineEnd: '-3rem',
        position: 'absolute',
        top: '-5rem',
        width: 260,
    },
    '&::after': {
        background: 'rgba(255, 255, 255, 0.09)',
        borderRadius: '55% 45% 0 0',
        bottom: '-7rem',
        content: '""',
        height: 210,
        insetInlineStart: '8%',
        position: 'absolute',
        transform: 'rotate(-5deg)',
        width: '65%',
    },
    [theme.breakpoints.down('sm')]: {
        borderRadius: 16,
        minHeight: 220,
    },
}));

export const CompanyLogoFrame = styled('div')(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '50%',
    boxShadow: '0 14px 34px rgba(16, 51, 75, 0.14)',
    display: 'flex',
    height: 138,
    justifyContent: 'center',
    margin: '-69px auto 0',
    overflow: 'hidden',
    padding: theme.spacing(0.75),
    position: 'relative',
    width: 138,
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
        height: 110,
        marginTop: -55,
        width: 110,
    },
}));

export const CompanyLogoImage = styled('img')({
    display: 'block',
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
});

export const CompanyLogoFallback = styled('div')(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.primary.extraLight,
    borderRadius: 14,
    color: theme.palette.primary.main,
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
    '& .MuiSvgIcon-root': {
        fontSize: '3rem',
    },
}));

export const CompanySummary = styled('div')(({ theme }) => ({
    marginInline: 'auto',
    maxWidth: 820,
    paddingTop: theme.spacing(2),
    textAlign: 'center',
}));

export const CompanyName = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.main,
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 800,
    lineHeight: 1.2,
}));

export const CompanyDescription = styled(Typography)(({ theme }) => ({
    color: '#536b7a',
    fontSize: '1.1rem',
    lineHeight: 1.8,
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-line',
    [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
        lineHeight: 1.65,
    },
}));

export const JobsSection = styled('section')(({ theme }) => ({
    marginTop: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(4),
    },
}));

export const JobsHeader = styled('div')(({ theme }) => ({
    alignItems: 'flex-end',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
}));

export const JobsTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.main,
    fontSize: 'clamp(1.7rem, 3vw, 2.25rem)',
    fontWeight: 800,
}));

export const ResultsCount = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.main,
    fontWeight: 700,
}));

export const FiltersPanel = styled('div')(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.primary.extraLight,
    border: `1px solid ${theme.palette.primary.main}1f`,
    borderRadius: 14,
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        '& > :last-child': {
            gridColumn: '1 / -1',
        },
    },
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        padding: theme.spacing(1.5),
        '& > :last-child': {
            gridColumn: 'auto',
        },
    },
}));

export const FilterControl = styled(FormControl)({
    minWidth: 0,
    '& .MuiInputBase-root': {
        backgroundColor: '#ffffff',
    },
});

export const ClearFiltersButton = styled(Button)(({ theme }) => ({
    minHeight: 56,
    minWidth: 150,
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
}));

export const PaginationWrapper = styled('nav')(({ theme }) => ({
    marginTop: theme.spacing(3),
    '& .MuiBox-root': {
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        justifyContent: 'center',
    },
}));

export const EmptyState = styled('div')(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    color: theme.palette.secondary.main,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    minHeight: 220,
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
        fontSize: '3rem',
    },
}));
